import React, { useState, useMemo, useCallback } from "react";
import { Flex, Separator, Text } from "@radix-ui/themes";
import { MoveToLiveModeCTA } from "@/routes/simulator/moveToLiveMode"; // this is a temp feature ment to be deleted
import { Topic } from "@/v2/entities/topic/Topic.model";
import { Instruction } from "@/v2/entities/instruction/Instruction.model";
import { useTopicDraft } from "../../context/useTopicDraft";
import { BriefDialogGuard } from "../briefDialog";
import { Knowledge } from "../knowledge";
import { useSimulator } from "../../context/SimulatorWrapper";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import { TopicOverrideConfig } from "../topicOverrideConfig";
import { TopicHeader } from "./InstructionsTopicHeader";
import { InstructionsPrerequisites } from "./InstructionsPrerequisites";
import { InstructionsBrief } from "./InstructionsBrief";
import { InstructionsInstructions } from "./InstructionsInstructions";
import { InstructionsFeedback } from "./InstructionsFeedback";
import { InstructionsSOPVisualizations } from "./InstructionsSOPVisualizations";
import { SimulatorChannel } from "@/api/simulator/simulator.model";
import { InteractionAdditionalContextGuard } from "@/v2/features/interaction-additional-context/InteractionAdditionalContext";
import { useTrainingAdditionalContextEnabled } from "@/v2/features/interaction-additional-context/useTrainingAdditionalContextEnabled";
import styles from "./styles.module.scss";

export const Instructions = ({
  topicId,
  topic,
  topics,
  setBriefOpen,
  channel,
  tenantGroup,
  customField,
  source,
}: {
  topicId: number;
  topic: string | null;
  topics: Topic[] | null;
  setBriefOpen: (value: boolean) => void;
  channel?: SimulatorChannel;
  tenantGroup?: string[];
  customField?: string[];
  source: string;
}) => {
  const sopVisualizations = useFlag("sopVisualizations");
  const showTrainingAdditionalContext =
    useTrainingAdditionalContextEnabled(channel);
  const [isOpen, setOpen] = useState(false);

  const { instructions, prerequisites, brief } = useTopicDraft({ topicId });
  const { currentRelease, publishedRelease, simulator } = useSimulator();

  const getInstructionStatus = useCallback(
    (instruction: Instruction) => {
      if (!publishedRelease?.publishedAt) {
        return {
          isNewInstruction: true,
          isEditedInstruction: false,
        };
      }

      const publishedAt = new Date(publishedRelease.publishedAt).getTime();
      const createdAt = new Date(instruction.createdAt).getTime();
      const updatedAt = new Date(instruction.updatedAt).getTime();

      const isNewInstruction = createdAt > publishedAt;
      const isEditedInstruction = !isNewInstruction && updatedAt > publishedAt;

      return {
        isNewInstruction,
        isEditedInstruction,
      };
    },
    [publishedRelease]
  );

  const trackingData = useMemo(
    () => ({
      source,
      channel: simulator?.channel,
      topic,
    }),
    [source, simulator?.channel, topic]
  );

  const topicConversations = useMemo(() => {
    return (
      currentRelease?.releaseConversations?.filter((conv) => {
        const convTopic = topics?.find(
          (t: Topic) => t.id === conv.topicId
        )?.topic;
        return convTopic === topic;
      }) || []
    );
  }, [currentRelease, topics, topic]);

  const goodCount = useMemo(() => {
    return topicConversations.filter((conv) => conv.feedback === "POSITIVE")
      .length;
  }, [topicConversations]);

  const badCount = useMemo(() => {
    return topicConversations.filter((conv) => conv.feedback === "NEGATIVE")
      .length;
  }, [topicConversations]);

  const noFeedbackCount = useMemo(() => {
    return topicConversations.filter(
      (conv) => conv.feedback !== "POSITIVE" && conv.feedback !== "NEGATIVE"
    ).length;
  }, [topicConversations]);

  const briefChanged = useMemo(() => {
    const publishedBrief = publishedRelease?.topicReleasePolicies?.find(
      (topic) => topic.topicId === topicId
    )?.brief;

    const currentBrief = currentRelease?.topicReleasePolicies?.find(
      (topic) => topic.topicId === topicId
    )?.brief;

    const normalizedPublishedBrief = publishedBrief || "";
    const normalizedCurrentBrief = currentBrief || "";

    return normalizedPublishedBrief !== normalizedCurrentBrief;
  }, [publishedRelease, topicId, brief]);

  if (!topic) {
    return (
      <div className={styles.emptyStateContainer}>
        <Text size={"1"} style={{ color: "var(--gray-9)" }}>
          Select a topic to view and manage its instructions
        </Text>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <TopicHeader topic={topic} topicId={topicId} />

      <MoveToLiveModeCTA
        topic={topics?.find((t) => t.topic === topic)?.id?.toString() || null}
        topics={topics}
        channel={channel}
        tenantGroup={tenantGroup}
        customField={customField}
      />

      <Separator size="4" style={{ backgroundColor: "var(--gray-4)" }} />

      <InstructionsPrerequisites
        topicId={topicId}
        prerequisites={prerequisites}
        trackingData={trackingData}
      />

      {sopVisualizations && <InstructionsSOPVisualizations topicId={topicId} />}

      <Separator
        size="4"
        style={{ margin: "16px", backgroundColor: "var(--gray-4)" }}
      />

      <InstructionsBrief
        brief={brief}
        briefChanged={briefChanged}
        setBriefOpen={setBriefOpen}
      />

      <Separator
        size="4"
        style={{ margin: "16px", backgroundColor: "var(--gray-4)" }}
      />

      <Knowledge topicId={topicId} />
      <>
        <Separator
          size="4"
          style={{ margin: "16px", backgroundColor: "var(--gray-4)" }}
        />
        <TopicOverrideConfig topicId={topicId} />
      </>

      <Separator
        size="4"
        style={{ margin: "16px", backgroundColor: "var(--gray-4)" }}
      />

      <InstructionsInstructions
        topicId={topicId}
        instructions={instructions}
        trackingData={trackingData}
        getInstructionStatus={getInstructionStatus}
      />

      <Separator
        size="4"
        style={{ margin: "16px", backgroundColor: "var(--gray-4)" }}
      />

      <InstructionsFeedback
        topic={topic}
        topics={topics}
        topicConversations={topicConversations}
        goodCount={goodCount}
        badCount={badCount}
        noFeedbackCount={noFeedbackCount}
      />

      {showTrainingAdditionalContext && (
        <>
          <Separator size="4" style={{ backgroundColor: "var(--gray-4)" }} />
          <InteractionAdditionalContextGuard channel={channel} />
        </>
      )}

      <BriefDialogGuard
        open={isOpen}
        openChange={(v) => {
          setOpen(v);
          setBriefOpen(v);
        }}
      />
    </div>
  );
};
